import { createPortal } from 'react-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { useParams } from 'react-router-dom';
import type { FieldArrayRenderProps } from 'formik';
import type { JSX } from 'react';

import Alert from '@mui/material/Alert';

import { ActorTypeEnum, getFragmentData } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import { isDefined } from '@e-socna/utils';
import {
  brokenCardinalityRulesOf,
  cardinalityRulesOf,
  representativesCarryingOf,
} from '@e-socna/folder-actors';
import type { Id, RepresentativeTypeId, UserRoleId } from '@e-socna/data-layer';

import {
  Actors,
  ActorsCmp_Actor_Fragment,
} from '../../../../../components/Actors';
import { RightDrawer } from '../../../../../components/RightDrawer';
import { useActionBlocker } from '../../../../../hooks/useActionBlocker.hook';
import { useWizardContext } from '../../../../../components/Wizard/contexts/WizardContext/hooks/useWizardContext.hook';
import { useReferencesContext } from '../../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useGraphQLQueries } from '../../../../../hooks/useGraphQLQueries.hook';
import { OrganizationPage_OneOrganization_QueryDocument } from '../../../../../pages/Organization/hooks/useFindOneOrganizationQuery.hook';
import { ActorCardCmp_Organization_Fragment } from '../../../../../components/Actors/Card/fragments/organization.fragment';
import { missingRequiredRoleIds } from '../../../../../components/Actors/utils/missing-required-roles.util';
import {
  missingRepresentativeTypeIds,
  quotationPartyOf,
  representativeTypesRefusalOf,
} from '../../../../../components/Actors/utils/missing-representative-types.util';
import type { RoleActor } from '../../../../../components/Actors/utils/missing-required-roles.util';

import { FormVariant } from '../../../../enums/variant.enum';
import { ActorForm } from '../../../../Actor';
import { effectiveBaseRoleFlags } from '../../../../Actor/contexts/base-role-flags.context';
import type { ActorForm_Actor } from '../../../../Actor/fragments/actor.fragment';

import { DeleteFolderBusinessEntityActorForm } from '../Delete';
import { ActorListsChangeDialog } from '../ActorListsChangeDialog';
import { ListInheritanceChange, ListInheritanceForm } from '../ListInheritance';
import type { Props } from '../types/props.type';
import type { ActorsContext } from '../types/context.type';
import { actorListKeyOf, actorListOf } from '../utils/actor-list.util';

import { useActorsReducer } from './useReducer.hook';
import { useFindActorIndexById } from './useFindIndexById.hook';
import {
  describeFreezes,
  useFreezeConfirmedMutations,
} from './useFreezeConfirmedMutations.hook';
import { useCreateFolderBusinessEntityActorSubmit } from './useCreateFolderBusinessEntityActorSubmit.hook';
import { useFlushFolderBusinessEntityActorsSubmit } from './useFlushFolderBusinessEntityActorsSubmit.hook';
import { useCustomizeFolderBusinessEntityActorsSubmit } from './useCustomizeFolderBusinessEntityActorsSubmit.hook';
import { useUpdateFolderBusinessEntityActorSubmit } from './useUpdateFolderBusinessEntityActorSubmit.hook';
import { useRemoveFolderBusinessEntityActorSubmit } from './useRemoveFolderBusinessEntityActorSubmit.hook';

export function useActorsFieldArrayRenderer(props: Readonly<Props>): Readonly<{
  fieldArrayRender: (props: Readonly<FieldArrayRenderProps>) => JSX.Element;
}> {
  const {
    categoryRestriction,
    creationRefusal,
    fallbackActors: pInheritableActors,
    isInternal,
    isUnderConfidentiality,
    mutations: pMutations,
    previewFreezes,
    paths,
    selectionRestriction,
    useDirectMutation,
    rules,
    notices,
  } = props;

  const {
    actorCategoriesCodeMap,
    genericStatusesCodeMap,
    representativeTypesIdMap,
    userRolesIdMap,
  } = useReferencesContext();

  const { values, setFieldValue } = useFormikContext<ActorsContext>();

  const folderBusinessEntityId = values._id;

  const { actorId: actorIdParam } = useParams();

  const { setIsSubmitLocked } = useWizardContext();

  // One internal actor at most: a parent list holding several is shown inherited all the same,
  // the rule telling to specify the list and keep one of them.
  const isOneInternal = isInternal && rules.atMostOneInternal;
  const pFallbackActors = pInheritableActors;
  const fallbackActors = getFragmentData(
    ActorsCmp_Actor_Fragment,
    pInheritableActors,
  );

  const canInherit = !!fallbackActors;
  const listKey = actorListKeyOf(isInternal);
  const list = actorListOf(isInternal);
  const actors = values[listKey];
  const mustInherit = canInherit && !actors;
  const actorsOrFallback = mustInherit ? fallbackActors : actors;
  const actorsAreMissing = !actorsOrFallback?.length;

  // The rules of the list edited, on its actors alone: the other list has its own tab.
  const errors = useMemo(
    () =>
      brokenCardinalityRulesOf(
        {
          internalActors: EMPTY_ARRAY,
          externalActors: EMPTY_ARRAY,
          [listKey]: actorsOrFallback ?? EMPTY_ARRAY,
        },
        cardinalityRulesOf(rules, {
          organizationKind: ActorTypeEnum.ActorOrganization,
          quotationEmitterCategoryId:
            actorCategoriesCodeMap.QuotationEmitter._id,
          quotationReceiverCategoryId:
            actorCategoriesCodeMap.QuotationReceiver._id,
        }),
      ).filter(({ list }) => list === listKey),
    [
      actorCategoriesCodeMap.QuotationEmitter._id,
      actorCategoriesCodeMap.QuotationReceiver._id,
      actorsOrFallback,
      listKey,
      rules,
    ],
  );

  // The roles the workflow requires of the internal actors and the representative types it
  // requires of the quotation party of the list, carried as the back computes them (the
  // memberships of the organizations, loaded by their cards too); without internal actor, the rule
  // of at least one tells what is missing.
  const requiredRoleIds = isInternal
    ? rules.requiredInternalContextualRoleIds
    : EMPTY_ARRAY;
  const quotationParty = useMemo(
    () =>
      quotationPartyOf(isInternal ? 'internal' : 'external', rules, {
        quotationEmitterCategoryId: actorCategoriesCodeMap.QuotationEmitter._id,
        quotationReceiverCategoryId:
          actorCategoriesCodeMap.QuotationReceiver._id,
      }),
    [
      actorCategoriesCodeMap.QuotationEmitter._id,
      actorCategoriesCodeMap.QuotationReceiver._id,
      isInternal,
      rules,
    ],
  );
  const hasRequirements = !!(
    requiredRoleIds.length || quotationParty.requiredTypeIds.length
  );
  const listActors = actorsOrFallback ?? EMPTY_ARRAY;
  const organizationResults = useGraphQLQueries(
    (hasRequirements ? (listActors as readonly RoleActor[]) : EMPTY_ARRAY)
      .filter(({ representatives }) => !!representatives)
      .map(({ thirdPartyId }) => ({
        document: OrganizationPage_OneOrganization_QueryDocument,
        variables: { id: thirdPartyId as Id },
      })),
  );
  const areOrganizationsLoaded = organizationResults.every(
    ({ isSuccess }) => isSuccess,
  );
  const organizations = useMemo(
    () =>
      organizationResults
        .map(({ data }) => data?.findOneOrganization)
        .filter(isDefined)
        .map((organization) =>
          getFragmentData(ActorCardCmp_Organization_Fragment, organization),
        ),
    [organizationResults],
  );
  const missingRoleIds = useMemo(
    () =>
      requiredRoleIds.length && listActors.length && areOrganizationsLoaded
        ? missingRequiredRoleIds({
            internalActors: listActors,
            organizations,
            enabledStatusId: genericStatusesCodeMap.Enabled._id,
            requiredRoleIds,
          })
        : EMPTY_ARRAY,
    [
      areOrganizationsLoaded,
      genericStatusesCodeMap.Enabled._id,
      listActors,
      organizations,
      requiredRoleIds,
    ],
  );
  const representativeTypesRefusal = useMemo(
    () =>
      quotationParty.requiredTypeIds.length && areOrganizationsLoaded
        ? representativeTypesRefusalOf(
            quotationParty,
            missingRepresentativeTypeIds({
              actors: listActors,
              organizations,
              enabledStatusId: genericStatusesCodeMap.Enabled._id,
              party: quotationParty,
            }),
            (typeId) =>
              representativeTypesIdMap[typeId as RepresentativeTypeId]?.label ??
              'Inconnu',
          )
        : null,
    [
      areOrganizationsLoaded,
      genericStatusesCodeMap.Enabled._id,
      listActors,
      organizations,
      quotationParty,
      representativeTypesIdMap,
    ],
  );

  useEffect(() => {
    setIsSubmitLocked(
      !!(
        !!creationRefusal ||
        actorsAreMissing ||
        errors.length ||
        missingRoleIds.length ||
        representativeTypesRefusal
      ),
    );

    return () => {
      setIsSubmitLocked(false);
    };
  }, [
    actorsAreMissing,
    creationRefusal,
    errors,
    missingRoleIds.length,
    representativeTypesRefusal,
    setIsSubmitLocked,
  ]);

  const {
    state,
    handleClearState,
    handleStartCreate,
    handleStartFlush,
    handleStartCustomize,
    handleStartRemove,
    handleStartUpdate,
    handleCreateSuccess,
    handleFlushSuccess,
    handleCustomizeSuccess,
    handleUpdateSuccess,
    handleRemoveSuccess,
  } = useActorsReducer(props);

  const {
    closeFrozenAlert,
    confirmedMutations: mutations,
    frozen,
    pendingChange,
    setPendingChange,
  } = useFreezeConfirmedMutations({
    mutations: pMutations,
    previewFreezes,
    lists: values,
  });
  const closePendingChange = useCallback(
    (isVisible: boolean) => {
      if (!isVisible) {
        setPendingChange(undefined);
      }
    },
    [setPendingChange],
  );
  const isCreateEnabled = !(isOneInternal && (actors?.length ?? 0) >= 1);

  const findActorIndexById = useFindActorIndexById(listKey);

  const initialValues =
    paths && actorIdParam
      ? actors?.[findActorIndexById(actorIdParam as Id)]
      : state.idToUpdate
        ? actors?.[findActorIndexById(state.idToUpdate)]
        : undefined;

  const { handleSubmitFactory: handleCreateSubmitFactory } =
    useCreateFolderBusinessEntityActorSubmit({
      folderBusinessEntityId,
      list,
      handleCreateSuccess,
      directMutation: useDirectMutation ? mutations.create : undefined,
    });

  const { handleSubmitFactory: handleFlushSubmitFactory } =
    useFlushFolderBusinessEntityActorsSubmit({
      folderBusinessEntityId,
      list,
      handleFlushSuccess,
      directMutation: useDirectMutation ? mutations.flush : undefined,
    });

  const { handleSubmitFactory: handleCustomizeSubmitFactory } =
    useCustomizeFolderBusinessEntityActorsSubmit({
      folderBusinessEntityId,
      list,
      handleCustomizeSuccess,
      directMutation: useDirectMutation ? mutations.customize : undefined,
    });

  const { handleSubmitFactory: handleRemoveSubmitFactory } =
    useRemoveFolderBusinessEntityActorSubmit({
      actorId: state.idToRemove,
      folderBusinessEntityId,
      list,
      directMutation: useDirectMutation ? mutations.remove : undefined,
      handleRemoveSuccess,
    });

  const { handleSubmitFactory: handleUpdateSubmitFactory } =
    useUpdateFolderBusinessEntityActorSubmit({
      actorId: state.idToUpdate,
      folderBusinessEntityId,
      list,
      directMutation: useDirectMutation ? mutations.update : undefined,
      handleUpdateSuccess,
    });

  const [blockableClearStateHandler, setMustBlockAction] =
    useActionBlocker(handleClearState);

  const fieldArrayRender = useCallback(
    (fieldArray: Readonly<FieldArrayRenderProps>) => {
      const handleCreateSubmit = handleCreateSubmitFactory({
        createInForm: (value: Readonly<ActorForm_Actor>) => {
          fieldArray.push({ ...value, index: actors?.length ?? 0 });
        },
      });
      const handleFlushSubmit = handleFlushSubmitFactory({
        flushInForm: () => {
          void setFieldValue(listKey, null);
        },
      });
      const handleCustomizeSubmit = handleCustomizeSubmitFactory({
        customizeInForm: () => {
          void setFieldValue(
            listKey,
            fallbackActors?.map((item, i) => ({ ...item, index: i })),
          );
        },
      });
      const handleRemoveSubmit = handleRemoveSubmitFactory({
        removeInForm: (removedIndex: number) => {
          const reindexed = (actors ?? [])
            .filter((_, i) => i !== removedIndex)
            .map((item, i) => ({ ...item, index: i }));
          void setFieldValue(listKey, reindexed);
        },
      });
      const handleUpdateSubmit = handleUpdateSubmitFactory({
        updateInForm: (index: number, value: Readonly<ActorForm_Actor>) => {
          fieldArray.replace(index, value);
        },
      });

      return (
        <>
          <Actors
            actionsEnabled
            actors={actors}
            confirmation={
              frozen.length ? (
                <Alert onClose={closeFrozenAlert} severity="success">
                  {`Ces niveaux gardent leurs listes précédentes en propre : ${describeFreezes(frozen)}.`}
                </Alert>
              ) : null
            }
            consultThirdPartyInNewTab
            errors={[
              creationRefusal,
              ...(actorsAreMissing
                ? []
                : errors.map(({ refusal }) => refusal.fr)),
              missingRoleIds.length
                ? `Chaque organisation interne doit posséder ${representativesCarryingOf(
                    'role',
                    missingRoleIds.map(
                      (roleId) =>
                        userRolesIdMap[roleId as UserRoleId]?.label ??
                        'Inconnu',
                    ),
                  )}.`
                : null,
              representativeTypesRefusal,
            ].filter(isDefined)}
            fallbackActors={pFallbackActors}
            isCreateEnabled={isCreateEnabled}
            isInternal={isInternal}
            notices={notices}
            onStartCreate={handleStartCreate}
            onStartCustomize={handleStartCustomize}
            onStartFlush={handleStartFlush}
            onStartRemove={handleStartRemove}
            onStartUpdate={handleStartUpdate}
            rules={rules}
          />
          {/* NB : We don't want to nest forms in HTML, so we extract these in a portal */}
          {createPortal(
            <>
              <ActorListsChangeDialog
                onClose={closePendingChange}
                pendingChange={pendingChange}
              />
              <DeleteFolderBusinessEntityActorForm
                askMemoBeforeSubmit={useDirectMutation}
                isDialogOpen={state.isRemoveDialogOpen}
                onCloseDialog={handleClearState}
                onSubmit={handleRemoveSubmit}
                useDirectMutation={useDirectMutation}
              />
              <ListInheritanceForm
                askMemoBeforeSubmit={useDirectMutation}
                change={ListInheritanceChange.Flush}
                isDialogOpen={state.isFlushDialogOpen}
                onCloseDialog={handleClearState}
                onSubmit={handleFlushSubmit}
                useDirectMutation={useDirectMutation}
              />
              <ListInheritanceForm
                askMemoBeforeSubmit={useDirectMutation}
                change={ListInheritanceChange.Customize}
                isDialogOpen={state.isCustomizeDialogOpen}
                onCloseDialog={handleClearState}
                onSubmit={handleCustomizeSubmit}
                useDirectMutation={useDirectMutation}
              />
            </>,
            document.body,
          )}
          <RightDrawer
            isOpen={state.isCreateDrawerOpen || state.isUpdateDrawerOpen}
            onDrawerClose={blockableClearStateHandler}
          >
            <ActorForm
              allowsInternalQuotationReceiver={
                !!rules.allowsInternalQuotationReceiver
              }
              askMemoBeforeSubmit={false}
              baseRoleFlags={effectiveBaseRoleFlags(
                rules,
                isUnderConfidentiality,
              )}
              categoryRestriction={categoryRestriction}
              excludedIds={actors
                ?.filter((actor) =>
                  state.isUpdateDrawerOpen
                    ? actor._id !== state.idToUpdate
                    : true,
                )
                .map(({ thirdPartyId }) => thirdPartyId)}
              formVariant={
                state.isUpdateDrawerOpen
                  ? FormVariant.Modification
                  : FormVariant.Creation
              }
              index={
                (state.isUpdateDrawerOpen
                  ? initialValues?.index
                  : actors?.length) ?? 0
              }
              initialValues={initialValues}
              isInternal={isInternal}
              onCancel={blockableClearStateHandler}
              onSubmit={
                state.isUpdateDrawerOpen
                  ? handleUpdateSubmit
                  : handleCreateSubmit
              }
              representationRules={rules}
              requiredRoleIds={rules.requiredInternalContextualRoleIds}
              selectionRestriction={selectionRestriction}
              setMustBlockAction={!paths ? setMustBlockAction : undefined}
              showCancelButton
              title={
                state.isUpdateDrawerOpen
                  ? "Formulaire de modification de l'Acteur"
                  : "Formulaire de création d'un Acteur"
              }
            />
          </RightDrawer>
        </>
      );
    },
    [
      blockableClearStateHandler,
      categoryRestriction,
      fallbackActors,
      handleClearState,
      handleCreateSubmitFactory,
      handleFlushSubmitFactory,
      handleCustomizeSubmitFactory,
      handleRemoveSubmitFactory,
      handleStartCreate,
      handleStartFlush,
      handleStartCustomize,
      handleStartRemove,
      handleStartUpdate,
      handleUpdateSubmitFactory,
      initialValues,
      isInternal,
      isUnderConfidentiality,
      paths,
      pFallbackActors,
      rules,
      selectionRestriction,
      setFieldValue,
      setMustBlockAction,
      state,
      useDirectMutation,
      actors,
      closeFrozenAlert,
      closePendingChange,
      creationRefusal,
      frozen,
      actorsAreMissing,
      errors,
      missingRoleIds,
      pendingChange,
      representativeTypesRefusal,
      userRolesIdMap,
      isCreateEnabled,
      listKey,
      notices,
    ],
  );

  return { fieldArrayRender };
}
